/**
 * Simulated participants.
 *
 * A facilitator can fill the roles of a real scenario with artificial participants to demonstrate
 * the platform — a room of one can still watch votes come in, a reveal land, a leaderboard move and
 * a report get written. Everything that decides *what* a simulated participant does lives here, as
 * pure functions over a seeded generator, so the same seed always produces the same room, the same
 * votes and the same timings. The Durable Object only schedules and applies the results.
 *
 * Simulated participants are always labelled as such — on the console, on the big screen, on the
 * phones and in the after-action report — and they carry no participant context (no geography,
 * no device, no address). A report that includes them says so in its first paragraph.
 */
import type { DecisionOption, OptionQuality } from "./scenario/schema";

/** How the simulated room tends to vote. */
export type SimulationTemperament = "cautious" | "mixed" | "rushed";
/** How quickly simulated votes arrive once a window opens. */
export type SimulationPace = "natural" | "quick" | "instant";

export const SIMULATION_TEMPERAMENTS: readonly SimulationTemperament[] = ["cautious", "mixed", "rushed"];
export const SIMULATION_PACES: readonly SimulationPace[] = ["natural", "quick", "instant"];
/** Enough to fill every role of the largest scenario twice; kept under the broadcast cap. */
export const MAX_SIMULATED_PER_SESSION = 40;

export interface SimulationRequest {
  count: number;
  temperament: SimulationTemperament;
  pace: SimulationPace;
  /** Optional; a fixed seed makes the whole run reproducible (screenshots, tests). */
  seed: number | null;
}

export const DEFAULT_SIMULATION: SimulationRequest = {
  count: 12,
  temperament: "mixed",
  pace: "natural",
  seed: null,
};

/* --------------------------------------- randomness --------------------------------------- */

/** mulberry32: small, fast, good enough for a demo room, and identical on every runtime. */
export function seededRandom(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Fold a string into a 32-bit seed (FNV-1a) so per-node, per-participant streams stay independent. */
export function hashSeed(...parts: (string | number)[]): number {
  let h = 0x811c9dc5;
  for (const part of parts) {
    const s = String(part);
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 0x01000193);
    }
    h ^= 0x9e3779b9;
  }
  return h >>> 0;
}

/* ------------------------------------------ people ------------------------------------------ */

/**
 * First name and initial, in a mix that reads like a real distributed team. Full surnames are
 * avoided on purpose: nothing here should look like a specific person.
 */
export const SIMULATED_NAMES: readonly string[] = [
  "Amara O.",
  "Tomás R.",
  "Yuki S.",
  "Priya N.",
  "Lars B.",
  "Fatima H.",
  "Diego M.",
  "Chloé D.",
  "Kwame A.",
  "Ingrid S.",
  "Mateo G.",
  "Aiko T.",
  "Noor K.",
  "Sean O.",
  "Zanele M.",
  "Rafael C.",
  "Hana P.",
  "Oliver W.",
  "Leila F.",
  "Jonas K.",
  "Mei L.",
  "Arjun V.",
  "Sofia R.",
  "Tariq B.",
  "Elena V.",
  "Kenji I.",
  "Ana P.",
  "Malik J.",
  "Freya N.",
  "Luca B.",
  "Amina D.",
  "Ethan C.",
  "Ximena T.",
  "Ibrahim S.",
  "Nadia R.",
  "Felix H.",
  "Sana Q.",
  "Marco E.",
  "Wei Z.",
  "Grace O.",
];

export interface SimulatedPerson {
  name: string;
  avatar: string;
  roleId: string | null;
}

/**
 * Build the roster: names drawn without repetition from a seeded shuffle, avatars likewise, and
 * roles filled breadth-first — every role gets one person before any role gets two — while
 * respecting each role's `maxPlayers`. Once every role is full the rest join without a role.
 */
export function buildRoster(
  count: number,
  roles: readonly { id: string; maxPlayers?: number }[],
  roleCounts: ReadonlyMap<string, number>,
  avatars: readonly string[],
  seed: number,
  taken: ReadonlySet<string> = new Set(),
): SimulatedPerson[] {
  const random = seededRandom(seed);
  const names = shuffle(
    SIMULATED_NAMES.filter((n) => !taken.has(n)),
    random,
  );
  const avatarPool = shuffle([...avatars], random);
  const counts = new Map(roleCounts);
  const out: SimulatedPerson[] = [];
  let roleCursor = 0;
  for (let i = 0; i < count; i++) {
    // Find the next role that still has room, scanning at most one full lap.
    let roleId: string | null = null;
    for (let lap = 0; lap < roles.length; lap++) {
      const role = roles[(roleCursor + lap) % roles.length]!;
      const n = counts.get(role.id) ?? 0;
      if (role.maxPlayers === undefined || n < role.maxPlayers) {
        roleId = role.id;
        counts.set(role.id, n + 1);
        roleCursor = (roleCursor + lap + 1) % roles.length;
        break;
      }
    }
    out.push({
      name: names[i % names.length] ?? `Participant ${i + 1}`,
      avatar: avatarPool[i % avatarPool.length] ?? "🙂",
      roleId,
    });
  }
  return out;
}

function shuffle<T>(items: T[], random: () => number): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [items[i], items[j]] = [items[j]!, items[i]!];
  }
  return items;
}

/* ------------------------------------------ choices ------------------------------------------ */

/** Relative weight of each outcome quality per temperament. */
const WEIGHTS: Record<SimulationTemperament, Record<OptionQuality, number>> = {
  cautious: { best: 55, good: 30, risky: 10, bad: 5 },
  mixed: { best: 35, good: 30, risky: 20, bad: 15 },
  rushed: { best: 15, good: 25, risky: 30, bad: 30 },
};

/** People lean toward the option that is their own job. */
const ROLE_PULL = 2.5;

/**
 * Choose an option the way a participant with this temperament and role plausibly would. The
 * weight of a quality is shared between the options that carry it, and an option whose `roleBonus`
 * names the participant's role is pulled forward.
 */
export function chooseOption(
  options: readonly DecisionOption[],
  temperament: SimulationTemperament,
  roleId: string | null,
  random: () => number,
): DecisionOption {
  const perQuality = new Map<OptionQuality, number>();
  for (const o of options) perQuality.set(o.quality, (perQuality.get(o.quality) ?? 0) + 1);
  const weights = options.map((o) => {
    const base = WEIGHTS[temperament][o.quality] / (perQuality.get(o.quality) ?? 1);
    return roleId && o.roleBonus.includes(roleId) ? base * ROLE_PULL : base;
  });
  return options[weightedIndex(weights, random)] ?? options[0]!;
}

/** Polls have no right answer; spread the room with a mild preference for the first options. */
export function choosePollOption<T extends { id: string }>(options: readonly T[], random: () => number): T {
  const weights = options.map((_, i) => 1 / (1 + i * 0.35));
  return options[weightedIndex(weights, random)] ?? options[0]!;
}

function weightedIndex(weights: number[], random: () => number): number {
  const total = weights.reduce((a, b) => a + b, 0);
  let r = random() * total;
  for (let i = 0; i < weights.length; i++) {
    r -= weights[i]!;
    if (r <= 0) return i;
  }
  return weights.length - 1;
}

/* ------------------------------------------ timing ------------------------------------------ */

/**
 * Where in the open window a participant votes, as a fraction of it. Cautious people read the
 * options; rushed people tap. `quick` compresses everything into the first quarter so a demo does
 * not wait out a ninety-second timer; `instant` is for tests.
 */
export function voteDelayFraction(
  temperament: SimulationTemperament,
  pace: SimulationPace,
  random: () => number,
): number {
  if (pace === "instant") return 0;
  const [lo, hi] =
    temperament === "cautious" ? [0.12, 0.7] : temperament === "rushed" ? [0.04, 0.4] : [0.08, 0.6];
  // Two draws averaged give a soft centre, like a room that mostly votes around the same moment.
  const f = lo + (hi - lo) * ((random() + random()) / 2);
  return pace === "quick" ? f * 0.35 : f;
}

/** The emoji a participant is likely to send when a decision of this quality is revealed. */
export function reactionFor(quality: OptionQuality, random: () => number): string | null {
  const r = random();
  if (r > 0.45) return null; // most people do not react
  const pool =
    quality === "best"
      ? ["👏", "✅", "🔥"]
      : quality === "good"
        ? ["✅", "👏"]
        : quality === "risky"
          ? ["🤔", "😬"]
          : ["😬", "🚩"];
  return pool[Math.floor(random() * pool.length)] ?? null;
}

/* ------------------------------------------ parsing ------------------------------------------ */

/** Coerce anything a client sent into a valid request; unknown values fall back to the defaults. */
export function parseSimulationRequest(input: unknown): SimulationRequest {
  const raw = (input && typeof input === "object" ? input : {}) as Record<string, unknown>;
  const count = Math.max(
    1,
    Math.min(MAX_SIMULATED_PER_SESSION, Math.floor(Number(raw.count) || DEFAULT_SIMULATION.count)),
  );
  const temperament = SIMULATION_TEMPERAMENTS.includes(raw.temperament as SimulationTemperament)
    ? (raw.temperament as SimulationTemperament)
    : DEFAULT_SIMULATION.temperament;
  const pace = SIMULATION_PACES.includes(raw.pace as SimulationPace)
    ? (raw.pace as SimulationPace)
    : DEFAULT_SIMULATION.pace;
  const seed = typeof raw.seed === "number" && Number.isFinite(raw.seed) ? Math.trunc(raw.seed) >>> 0 : null;
  return { count, temperament, pace, seed };
}
